import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { Header } from '@/components/header';
import { Button, ButtonText } from '@/components/ui/button';
import { Heading } from '@/components/ui/heading';
import { Text } from '@/components/ui/text';
import { VStack } from '@/components/ui/vstack';
import { SUPPORT_EMAIL_LINK } from '@/constants/links';
import { ROUTES } from '@/constants/routes';
import { useContactSupport } from '@/features/support-contact';
import { useColors } from '@/hooks/useColors';
import { useEmailLink } from '@/hooks/useEmailLink';

/**
 * Delete Account Screen
 *
 * Asks the member to tell support why they are leaving. A member who may hold a
 * support thread is sent there with a draft started; anyone else, such as an
 * account still in confirmation, is sent to email.
 */
export default function DeleteAccount() {
  const { colors } = useColors();

  const { openEmailLink } = useEmailLink();

  const { canUseThread, contact, isOpening } = useContactSupport({ overTabs: true });

  const { bottom } = useSafeAreaInsets();

  return (
    <VStack className="flex-1">
      <Header
        title="Delete Account"
        statusBarBackgroundColor={colors.background[0]}
        backRoute={ROUTES.ACCOUNT}
        isBackButton
      />

      <VStack
        className="flex-1 justify-between px-6 py-8"
        style={{ paddingBottom: Math.max(bottom, 24) }}>
        <VStack className="mx-auto w-full max-w-md" space="lg">
          <Text className="text-typography-700">
            We're sorry to see you go! Please{' '}
            {canUseThread ? 'send us a message' : 'send us an email'} with a brief description of
            why you're looking to delete your account. Our support team will be happy to help you
            close your account.
          </Text>

          <Text className="text-typography-700">
            Please note, once your account is deleted, you will no longer be able to access your
            Primum curbsides.
          </Text>
        </VStack>

        <VStack className="mx-auto w-full max-w-md items-center">
          <Button
            isDisabled={isOpening}
            onPress={() => {
              if (canUseThread) {
                contact({
                  source: 'delete_account',
                  draft: 'I’d like to delete my account because ',
                });

                return;
              }

              void openEmailLink(SUPPORT_EMAIL_LINK);
            }}>
            <ButtonText>{canUseThread ? 'Message Support' : 'Email Support'}</ButtonText>
          </Button>
        </VStack>
      </VStack>
    </VStack>
  );
}
